// Chat Service - Handles all API interactions for the chat widget

// API Configuration
// Use environment variable for API base URL, with fallback to dev API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

// Log environment info (only in development)
if (ENVIRONMENT === 'development') {
  console.log('Chat Widget Environment:', ENVIRONMENT);
}


/**
 * ChatService - Main service class for chat functionality
 */
export class ChatService {
  // Static properties to store user_id or session_id
  static userId = null;
  static sessionId = null;
  static SESSION_STORAGE_KEY = 'enterone_chat_session_id';

  /**
   * Initialize session from localStorage
   * Call this to load existing session_id from storage
   */
  static initializeSession() {
    try {
      // Load session_id from localStorage if it exists
      const storedSessionId = localStorage.getItem(this.SESSION_STORAGE_KEY);
      if (storedSessionId && !this.sessionId) {
        this.sessionId = storedSessionId;
      }
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
    }
  }

  /**
   * Save session_id to localStorage
   */
  static saveSessionToStorage(sessionId) {
    if (sessionId) {
      try {
        localStorage.setItem(this.SESSION_STORAGE_KEY, sessionId);
      } catch (error) {
        console.error('Failed to save session_id to localStorage:', error);
      }
    }
  }

  /**
   * Clear session from localStorage
   */
  static clearSessionFromStorage() {
    localStorage.removeItem(this.SESSION_STORAGE_KEY);
  }

  /**
   * Send a message to the bot and get a response
   * @param {string} message - User's message
   * @param {string} [userId] - Optional user ID (provided when user is logged in on client site)
   * @returns {Promise<Object>} Bot response with message and suggestions
   */
  static async sendMessage(message, userId = null) {
    try {
      // Initialize session from localStorage if not already loaded
      if (!this.sessionId && !this.userId) {
        this.initializeSession();
      }

      // Build request body with query
      const requestBody = {
        query: message,
      };

      // Priority 1: Use provided userId parameter (when user is logged in on client site)
      if (userId) {
        userId = String(userId);
        requestBody.user_id = userId;
        this.userId = userId;
        // User logged in, we prioritize user_id over session_id
      }
      // Priority 2: Use stored user_id from previous response
      else if (this.userId) {
        requestBody.user_id = this.userId;
      }
      // Priority 3: Use stored session_id (from memory or localStorage)
      else if (this.sessionId) {
        requestBody.session_id = this.sessionId;
      }

      // Call the real API
      const apiResponse = await fetch(`${API_BASE_URL}/agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!apiResponse.ok) {
        throw new Error(`API request failed with status: ${apiResponse.status}`);
      }

      const data = await apiResponse.json();
      
      // Store user_id or session_id from response for subsequent calls
      if (data.user_id) {
        // User is logged in - store user_id
        this.userId = data.user_id;
        // Keep session_id in localStorage for when user logs out
        // Don't clear it, just deprioritize it
      }
      
      // Always store session_id if provided (even if user_id exists)
      if (data.session_id) {
        // Store session_id in memory and localStorage
        this.sessionId = data.session_id;
        // Save session_id to localStorage for persistence
        this.saveSessionToStorage(data.session_id);
      }
      
      // Transform API response to match expected format
      // API returns: { response: "message text", user_id?: string, session_id?: string }
      // Chat widget expects: { message: string, suggestions?: string[], links?: array }
      return {
        message: data.response || "I'm sorry, I didn't receive a proper response. Please try again.",
        suggestions: [], // API doesn't provide suggestions, so we use empty array
        links: [] // API doesn't provide links, so we use empty array
      };
    } catch (error) {
      console.error('Error calling chat API:', error);
      
      // Fallback to error message
      return {
        message: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
        suggestions: ['Try again', 'Contact support'],
        links: [
          {
            text: "Contact Support",
            url: "https://www.enterone.com/contact_us",
            external: true
          }
        ]
      };
    }
  }

  /**
   * Handle user logout - clear user_id but keep session_id
   * This allows the user to continue with their session after logout
   */
  static handleUserLogout() {
    this.userId = null;
    // sessionId remains in memory and localStorage
    // Load from localStorage if needed
    if (!this.sessionId) {
      this.initializeSession();
    }
  }

  /**
   * Reset session completely - clears everything including localStorage
   * Use this for a completely fresh start
   */
  static resetSession() {
    this.userId = null;
    this.sessionId = null;
    this.clearSessionFromStorage();
  }

  /**
   * Get chat history
   * @returns {Promise<Array>} Array of chat messages in widget format
   */
  static async getChatHistory() {
    try {
      // Call the real API to fetch chat history
      const apiResponse = await fetch(
        `${API_BASE_URL}/chatlogs/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!apiResponse.ok) {
        throw new Error(`API request failed with status: ${apiResponse.status}`);
      }

      const data = await apiResponse.json();
      
      // Transform API response to match widget message format
      // API returns: { response: [{ role: "user"|"assistant", content: string, timestamp: string }, ...] }
      // Widget expects: [{ id: string, type: "user"|"bot", text: string, timestamp: Date, links?: array }, ...]
      
      if (!data.response || !Array.isArray(data.response)) {
        return [];
      }

      return data.response.map((msg, index) => ({
        id: `history-${index}-${Date.now()}`,
        type: msg.role === 'user' ? 'user' : 'bot',
        text: msg.content || '',
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        links: [] // API doesn't provide links in history
      }));
    } catch (error) {
      console.error('Error fetching chat history:', error);
      // Return empty array on error (no history to show)
      return [];
    }
  }
}

// Legacy function for backward compatibility
export const getBotResponse = async (userMessage, userId = null) => {
  return await ChatService.sendMessage(userMessage, userId);
};


